// What the one PlayerBar (MiniPlayer on phones) shows, and who owns the
// speakers (SI-3, SI-4). Two engines register here:
//   "session"   audio/player.js, a session's Play all
//   "audition"  audio/audition.js, sounds, layer rows, previews, Discover
//
// Arbitration:
//   - An audition starting while a session is loaded PAUSES the session
//     (never stops it): its position stays, `suspended` holds its item, and
//     the layer rows keep their mix playheads.
//   - When the audition stops or ends, the bar returns to the suspended
//     session, paused; play resumes it.
//   - Play all starting stops any audition (previews have nothing to
//     resume).
//
// state = { source: null | "session" | "audition", item, playing,
//           buffering, progress, loaded, canPrev, canNext, hasSteps,
//           suspended }
// hasSteps: whether the bar shows ⏮ ⏭ at all. A looping session's Play
// all has neither (3.5); everything else shows them, live or not.
// item  = { id, title, subtitle, art: { seed, owner, picture }, color,
//           waveform: { hash } | { src }, meta,
//           cta: { label, kind: "primary" | "tinted", disabled, onClick } | null,
//           addToSession: fn | null, dragHandle: fn | null,
//           pricePill: { label, onClick } | null, share: fn | null }
//
// No imports beyond Vue, so tests can drive it with fake engines.
import { reactive } from "vue";

const core = reactive({ source: null, item: null, suspended: null });
const engines = {};

const engine = () => (core.source ? engines[core.source] : null);
const read = (name, fallback) => {
  const e = engine();
  if (!e || typeof e[name] !== "function") return fallback;
  return e[name]();
};

export const state = {
  get source() {
    return core.source;
  },
  get item() {
    return core.item;
  },
  get suspended() {
    return core.suspended;
  },
  get playing() {
    return !!read("playing", false);
  },
  get buffering() {
    return !!read("buffering", false);
  },
  get progress() {
    return Number(read("progress", 0)) || 0;
  },
  get loaded() {
    return Number(read("loaded", 0)) || 0;
  },
  get canPrev() {
    return !!read("canPrev", false);
  },
  get canNext() {
    return !!read("canNext", false);
  },
  get hasSteps() {
    return read("hasSteps", true) !== false;
  },
};

export function registerEngine(name, api) {
  engines[name] = api;
}

// The session engine started (Play all) or resumed: it owns the bar, and
// any audition stops.
export function sessionStarted(item) {
  if (core.source === "audition" && engines.audition) engines.audition.stop({ silent: true });
  core.source = "session";
  core.item = item || core.item;
  core.suspended = null;
}

// The session engine's item changed (a playlist step) while it owns the bar.
export function sessionUpdated(item) {
  if (core.source === "session") core.item = item;
  else if (core.suspended) core.suspended = item;
}

// The session engine stopped (unloaded).
export function sessionStopped() {
  if (core.source === "session") {
    core.source = null;
    core.item = null;
  }
  core.suspended = null;
}

// An audition started: a loaded session pauses and waits in `suspended`.
export function auditionStarted(item) {
  const session = engines.session;
  if (core.source === "session" && core.item) {
    core.suspended = core.item;
    if (session && session.playing && session.playing()) session.pause();
  } else if (core.source !== "audition" && session && session.hasSession && session.hasSession() && session.item) {
    core.suspended = session.item();
    if (session.playing && session.playing()) session.pause();
  }
  core.source = "audition";
  core.item = item;
}

// The audition's item changed (next or previous in its queue).
export function auditionUpdated(item) {
  if (core.source === "audition") core.item = item;
}

// The audition stopped or ended: back to the suspended session, paused.
export function auditionEnded() {
  if (core.source !== "audition") return;
  if (core.suspended) {
    core.source = "session";
    core.item = core.suspended;
    core.suspended = null;
  } else {
    core.source = null;
    core.item = null;
  }
}

// The bar's controls act on whoever owns it.
export function toggle() {
  const e = engine();
  if (!e) return;
  if (state.playing) e.pause();
  else e.resume();
}
export function pause() {
  const e = engine();
  if (e) e.pause();
}
export function resume() {
  const e = engine();
  if (e) e.resume();
}
export function stop() {
  const e = engine();
  if (e) e.stop();
}
export function seek(frac) {
  const e = engine();
  if (e && e.seek) e.seek(Math.max(0, Math.min(1, Number(frac) || 0)));
}
export function next() {
  const e = engine();
  if (e && e.next) e.next();
}
export function prev() {
  const e = engine();
  if (e && e.prev) e.prev();
}

// Signing out, or in as someone else: both engines stop and unload, and
// the bar forgets its item and the suspended session (their items carry
// the last account's tokens and actions). App.vue's loggedInUser watcher
// calls this on every sign-out path.
export function stopAll() {
  const audition = engines.audition;
  const session = engines.session;
  if (audition) {
    if (typeof audition.unload === "function") audition.unload();
    else audition.stop({ silent: true });
  }
  if (session) {
    if (typeof session.unload === "function") session.unload();
    else session.stop();
  }
  core.source = null;
  core.item = null;
  core.suspended = null;
}

// Tests: forget everything.
export function resetNowPlaying() {
  core.source = null;
  core.item = null;
  core.suspended = null;
  for (const k of Object.keys(engines)) delete engines[k];
}

const nowPlaying = {
  state,
  registerEngine,
  sessionStarted,
  sessionUpdated,
  sessionStopped,
  auditionStarted,
  auditionUpdated,
  auditionEnded,
  toggle,
  pause,
  resume,
  stop,
  seek,
  next,
  prev,
  stopAll,
};
export default nowPlaying;
